import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useTranslation } from "react-i18next";
import { useMutation } from "@tanstack/react-query";
import { AxiosError } from "axios";

import { authApi, isOtpRequired } from "@/api/auth";
import { useAuthStore } from "@/stores/auth";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";

const loginSchema = z.object({
  email: z.string().email(),
  password: z.string().min(1),
});
type LoginFormData = z.infer<typeof loginSchema>;

export function LoginPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const setTokens = useAuthStore((s) => s.setTokens);

  const [serverError, setServerError] = useState<string | null>(null);

  // Якщо нас редиректнули з token_invalidated — показуємо причину.
  useEffect(() => {
    const reason = params.get("reason");
    if (reason) {
      const known = [
        "session_invalidated",
        "token_version_mismatch",
        "user_inactive",
      ];
      const key = known.includes(reason) ? reason : "session_invalidated";
      setServerError(t(`login.errors.${key}`));
    }
  }, [params, t]);

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    mode: "onChange",
  });

  const loginMutation = useMutation({
    mutationFn: ({ email, password }: LoginFormData) => authApi.login(email, password),
    onSuccess: (data) => {
      if (isOtpRequired(data)) {
        navigate(`/login/otp?sid=${data.otp_session_id}&uid=${data.user_id}`, { replace: true });
        return;
      }
      setTokens(data.access_token, data.refresh_token);
      navigate("/", { replace: true });
    },
    onError: (err: AxiosError<{ error?: { code?: string } }>) => {
      const code = err.response?.data?.error?.code;
      if (code === "invalid_credentials") {
        setServerError(t("login.errors.invalid_credentials"));
      } else if (code === "user_inactive") {
        setServerError(t("login.errors.user_inactive"));
      } else if (code === "rate_limit_login") {
        setServerError(t("login.errors.rate_limit"));
      } else if (code === "otp_delivery_failed") {
        setServerError(t("login.errors.otp_delivery_failed"));
      } else if (err.code === "ERR_NETWORK") {
        setServerError(t("login.errors.network"));
      } else {
        setServerError(t("login.errors.unknown"));
      }
    },
  });

  const onSubmit = (data: LoginFormData) => {
    setServerError(null);
    loginMutation.mutate(data);
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center gap-8 p-4
                    bg-gradient-to-br from-brand-navy-50 via-white to-brand-mint-50">
      <Logo variant="full" height={90} />

      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>{t("login.title")}</CardTitle>
          <CardDescription>{t("login.subtitle")}</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
            <div className="space-y-2">
              <Label htmlFor="email">{t("login.email")}</Label>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                placeholder={t("login.email_placeholder")}
                aria-invalid={!!errors.email}
                {...register("email")}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">{t("login.password")}</Label>
              <Input
                id="password"
                type="password"
                autoComplete="current-password"
                aria-invalid={!!errors.password}
                {...register("password")}
              />
            </div>

            {serverError && (
              <div
                role="alert"
                className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
              >
                {serverError}
              </div>
            )}

            <Button
              type="submit"
              size="lg"
              className="w-full"
              disabled={!isValid || loginMutation.isPending}
            >
              {loginMutation.isPending ? t("login.submitting") : t("login.submit")}
            </Button>
          </form>

          {/* Перемикач мови — приховано на login до Phase 1a, але тримаємо UI самодостатнім. */}
          <div className="mt-6 flex justify-center gap-2 text-xs text-slate-500">
            <button
              type="button"
              onClick={() => i18n.changeLanguage("uk")}
              className={i18n.language === "uk" ? "font-semibold text-brand-navy-700" : "hover:underline"}
            >
              UK
            </button>
            <span>·</span>
            <button
              type="button"
              onClick={() => i18n.changeLanguage("ru")}
              className={i18n.language === "ru" ? "font-semibold text-brand-navy-700" : "hover:underline"}
            >
              RU
            </button>
          </div>
        </CardContent>
      </Card>

      <div className="text-xs text-slate-400">work.sphereenergy.solar</div>
    </div>
  );
}
